import React from 'react';
import { Wifi, WifiOff, RefreshCw, Cloud, CloudOff, Smartphone, Signal } from 'lucide-react';
import { useOffline } from '../context/OfflineContext';

const ConnectionStatus = () => {
  const {
    isOnline, connectionType, isWifi, isCellular,
    pendingCount, isSyncing, syncPendingActions, lastSync,
  } = useOffline();

  // Don't show anything if online and no pending actions
  if (isOnline && pendingCount === 0) return null;

  const connectionLabel = isOnline
    ? (isWifi ? 'Wi-Fi' : isCellular ? 'Cellulaire' : 'En ligne')
    : 'Hors ligne';

  const ConnectionIcon = isOnline
    ? (isWifi ? Wifi : isCellular ? Signal : Wifi)
    : WifiOff;

  const timeSinceSync = lastSync
    ? `${Math.round((Date.now() - lastSync.getTime()) / 60000)} min`
    : null;

  return (
    <div
      data-testid="connection-status-bar"
      className={`fixed top-0 left-0 right-0 z-50 px-4 py-2 flex items-center justify-between text-sm transition-all duration-300 ${
        isOnline
          ? 'bg-gradient-to-r from-green-600 to-emerald-600'
          : 'bg-gradient-to-r from-orange-600 to-red-600'
      }`}
    >
      <div className="flex items-center gap-2">
        <ConnectionIcon className={`w-4 h-4 text-white ${!isOnline ? 'animate-pulse' : ''}`} />
        <span data-testid="connection-label" className="text-white font-medium">
          {connectionLabel}
        </span>

        {isCellular && isOnline && (
          <span className="px-1.5 py-0.5 bg-white/20 rounded text-white/90 text-xs">
            <Smartphone className="w-3 h-3 inline mr-0.5" />reseau mobile
          </span>
        )}

        {pendingCount > 0 && (
          <span data-testid="pending-count" className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-white text-xs">
            {pendingCount} en attente
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {!isOnline && (
          <span className="text-white/80 text-xs hidden sm:inline">
            Donnees sauvegardees localement
            {timeSinceSync && ` · Sync il y a ${timeSinceSync}`}
          </span>
        )}

        {isOnline && pendingCount > 0 && (
          <button
            data-testid="sync-button"
            onClick={syncPendingActions}
            disabled={isSyncing}
            className="flex items-center gap-1 px-2 py-1 bg-white/20 rounded-md text-white text-xs hover:bg-white/30 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Sync...' : 'Synchroniser'}
          </button>
        )}
      </div>
    </div>
  );
};

// Compact indicator for use in headers / nav
export const ConnectionIndicator = () => {
  const { isOnline, connectionType, pendingCount } = useOffline();

  const Icon = isOnline ? Cloud : CloudOff;
  const color = isOnline ? 'text-green-400' : 'text-orange-400';

  return (
    <div data-testid="connection-indicator" className="flex items-center gap-1" title={`Connexion: ${connectionType}`}>
      <Icon className={`w-4 h-4 ${color} ${!isOnline ? 'animate-pulse' : ''}`} />
      {pendingCount > 0 && (
        <span className="w-4 h-4 flex items-center justify-center bg-orange-500 text-white text-xs rounded-full">
          {pendingCount}
        </span>
      )}
    </div>
  );
};

export default ConnectionStatus;
